// src/components/TablePagination.tsx
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import DropDown from "./DropDown";
import { getFontAwesomeIcon } from "../../utils";
import onlyAllowNumberValue from "../../utils/onlyAllowNumber";

interface PageNavigationButtonProps {
	icon: string;
	onClick: () => void;
	disabled: boolean;
}

interface TablePaginationProps {
	pageSize: number;
	setPageSize: (newSize: number) => void;
	currentPage: number;
	setCurrentPageInParent: (newPage: number) => void;
	setCurrentPageAndUpdateUrl: (newPage: number) => void;
	rowsPerPage: number[];
	totalRecordCount: number;
	slug: string;
	showSlideMenu?: boolean;
	renderDropDown?: boolean;
}

function getPageRange(
	currentPage: number,
	pageSize: number,
	totalRecordCount: number
): string {
	const start = (currentPage - 1) * pageSize + 1;
	const end = Math.min(currentPage * pageSize, totalRecordCount);
	return `${start} – ${end}`;
}

const PageNavigationButton: React.FC<PageNavigationButtonProps> = ({
	icon,
	onClick,
	disabled,
}) => (
	<button
		disabled={disabled}
		onClick={onClick}
		className={`${
			disabled
				? "border-200"
				: "border-navy-200 hover:bg-primaryVeryLight hover:border-primary cursor-pointer"
		} bg-white flex justify-center w-full border-1 rounded p-1 min-w-7`}
	>
		<div
			className={`${
				disabled ? "text-navy-200" : "text-primary"
			} text-xs flex p-1 items-center justify-center w-full`}
		>
			{getFontAwesomeIcon(icon, "solid")}
		</div>
	</button>
);

const TablePagination: React.FC<TablePaginationProps> = ({
	pageSize,
	setPageSize,
	currentPage,
	setCurrentPageInParent,
	setCurrentPageAndUpdateUrl,
	rowsPerPage,
	totalRecordCount,
	slug,
	showSlideMenu = false,
	renderDropDown = true,
}) => {
	const navigate = useNavigate();
	const { search } = useLocation();
	const urlSizeStr = new URLSearchParams(search).get(
		`${slug}_recordsPerPage`
	);
	const params = new URLSearchParams(search);
	const initialSize = urlSizeStr ? Number(urlSizeStr) : pageSize;
	useLayoutEffect(() => {
		if (initialSize !== pageSize) setPageSize(initialSize);
	}, []); // run once on mount
	const [inputPageSize, setInputPageSize] = useState(initialSize);

	const urlPage = params.get(`${slug}_page`);
	const [inputPage, setInputPage] = useState(urlPage ?? String(currentPage));

	// 1) compute pages from record count
	const totalPages = Math.ceil(totalRecordCount / pageSize);

	// 2) helper to jump pages
	const goTo = (page: number) => {
		setCurrentPageInParent(page);
		setCurrentPageAndUpdateUrl(page);
	};

	// 3) on pageSize change: reset to page 1 and update URL
	const onPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newSize = Number(e.target.value);
		setInputPageSize(newSize); // update our local controlled value
		setPageSize(newSize); // inform parent
		goTo(1);

		const params = new URLSearchParams(window.location.search);
		params.set(`${slug}_recordsPerPage`, String(newSize));
		params.set(`${slug}_page`, "1");
		navigate(`?${params.toString()}`, { replace: true });
	};

	return (
		<div className="flex w-full bg-white relative z-40 max-h-[70px] bottom-[-3px] rounded-lg">
			<div className="w-full flex flex-col sm:flex-row items-center gap-8 justify-between pt-4 border-t-1 border-b-1 border-navy-200 p-5">
				{/* Rows-per-page dropdown */}
				{renderDropDown && (
					<div className="ut-per-page inline-flex items-baseline order-2 sm:order-1 sm:w-auto w-full">
						<DropDown
							key={pageSize}
							defaultValue={pageSize}
							value={inputPageSize}
							onChange={onPageSizeChange}
							options={rowsPerPage}
							additionalClasses=""
							itemsTableDropdown
							position="up"
						/>
						{!showSlideMenu && (
							<span className="text-navy-270 pl-2">per page</span>
						)}
					</div>
				)}

				{/* Range text + nav */}
				<div className="flex text-sm items-center gap-2 order-1 sm:order-2 sm:w-auto w-full justify-between">
					<div className="flex">
						<span>
							{getPageRange(
								currentPage,
								pageSize,
								totalRecordCount
							)}
						</span>
						<div className="text-navy-300 pr-4">
							<span className="px-1">of</span>
							<span>{totalRecordCount}</span>
						</div>
					</div>

					{/* First / Prev */}
					<div className="gap-2 flex items-center justify-between">
						<PageNavigationButton
							icon="chevronsLeft"
							onClick={() => goTo(1)}
							disabled={currentPage === 1}
						/>
						<PageNavigationButton
							icon="chevronLeft"
							onClick={() => goTo(currentPage - 1)}
							disabled={currentPage === 1}
						/>
					</div>

					{/* Page input */}
					<div className="max-w-20 flex relative overflow-hidden group hover:cursor-pointer">
						<input
							className="h-[31px] border-1 w-full border-navy-200 rounded size-7 text-center focus:outline-none focus:border-primary hover:border-primary hover:bg-primaryVeryLight hover:text-primary group-hover:border-primary focus:pr-8 focus:pl-1 transition-all duration-100"
							type="text"
							// defaultValue={currentPage}
							value={inputPage}
							onChange={(e) =>
								onlyAllowNumberValue(e, (val) => {
									// 1) keep the input in sync
									setInputPage(val);
								})
							}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									const val = Number(
										(e.target as HTMLInputElement).value
									);
									const page = Math.min(
										Math.max(val || 1, 1),
										totalPages
									);
									// 2) navigate + parent state
									goTo(page);
									// 3) reset the input to match the actual page
									setInputPage(String(page));
								}
							}}
						/>
						<div
							onClick={() => {
								const input = (
									document.activeElement as HTMLInputElement
								).value;
								const val = Number(input) || 1;
								goTo(Math.min(val, totalPages));
							}}
							className="border-1 bg-white text-primary hover:bg-primaryVeryLight border-primary top-0 flex items-center justify-center right-0 absolute h-full w-1/3 rounded-tr rounded-br opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-0"
						>
							{getFontAwesomeIcon("check", "regular")}
						</div>
					</div>

					{/* of totalPages */}
					<div className="flex justify-between text-navy-300">
						<span>of {totalPages}</span>
					</div>

					{/* Next / Last */}
					<div className="flex items-center justify-between gap-2">
						<PageNavigationButton
							icon="chevronRight"
							onClick={() => goTo(currentPage + 1)}
							disabled={currentPage === totalPages}
						/>
						<PageNavigationButton
							icon="chevronsRight"
							onClick={() => goTo(totalPages)}
							disabled={currentPage === totalPages}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TablePagination;
